import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { BarChart3, BookOpen, Calendar, DollarSign, GraduationCap, LayoutDashboard, LogOut, Menu, Settings, Sparkles, Users } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { ThemeToggle } from "@/components/common/ThemeToggle";
import { UserAvatar } from "@/components/common/PhotoUpload";
import { NotificationsBell } from "@/components/layout/NotificationsBell";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, key: "dashboard" },
  { to: "/agenda", label: "Agenda", icon: Calendar, key: "agenda" },
  { to: "/alunos", label: "Alunos", icon: Users, key: "alunos" },
  { to: "/aulas", label: "Aulas", icon: BookOpen, key: "aulas" },
  { to: "/financeiro", label: "Financeiro", icon: DollarSign, key: "financeiro" },
  { to: "/relatorios", label: "Relatórios", icon: BarChart3, key: "relatorios" },
  { to: "/configuracoes", label: "Configurações", icon: Settings, key: "configuracoes" },
];
const MOBILE = ["dashboard", "agenda", "alunos", "financeiro"];

const Brand = () => (
  <div className="flex items-center gap-2">
    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
      <GraduationCap className="h-5 w-5" />
    </div>
    <span className="font-heading text-lg font-bold tracking-tight">ProfessorHub</span>
  </div>
);

const navClass = ({ isActive }) =>
  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`;

const VerifyBanner = () => {
  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-verification");
      toast.success(data.message);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mb-4 flex flex-col gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between" data-testid="verify-email-banner">
      <span>Confirme seu e-mail para manter sua conta segura.</span>
      <button onClick={resend} className="font-semibold text-amber-700 hover:underline dark:text-amber-400" data-testid="resend-verification-btn">
        Reenviar e-mail
      </button>
    </div>
  );
};

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [more, setMore] = useState(false);
  const name = `${user.first_name} ${user.last_name}`;

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed bottom-0 left-0 top-0 z-40 hidden w-64 flex-col border-r bg-card/80 backdrop-blur-md md:flex" data-testid="desktop-sidebar">
        <div className="px-5 py-5"><Brand /></div>
        <nav className="flex-1 space-y-1 px-3">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={navClass} data-testid={`nav-${n.key}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="m-3 rounded-xl border bg-background/60 p-3">
          <div className="flex items-center gap-3">
            <UserAvatar path={user.photo_path} name={name} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold" data-testid="sidebar-user-name">{name}</p>
              <p className="text-xs text-muted-foreground" data-testid="sidebar-user-plan">Plano {user.plan?.name}</p>
            </div>
            <button onClick={doLogout} className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Sair" data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
          {user.plan?.id !== "pro" && (
            <NavLink to="/planos" className="mt-3 flex items-center justify-center gap-2 rounded-lg bg-accent/15 px-3 py-2 text-xs font-semibold text-accent" data-testid="sidebar-upgrade-link">
              <Sparkles className="h-3.5 w-3.5" /> Fazer upgrade
            </NavLink>
          )}
        </div>
      </aside>

      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-background/80 px-4 backdrop-blur-md sm:px-6 md:ml-64 lg:px-8">
        <div className="md:hidden"><Brand /></div>
        <div className="hidden text-sm text-muted-foreground md:block">Olá, <span className="font-semibold text-foreground">{user.first_name}</span></div>
        <div className="flex items-center gap-2">
          <NotificationsBell />
          <ThemeToggle />
        </div>
      </header>

      <main className="overflow-x-hidden px-4 pb-24 pt-6 sm:px-6 md:ml-64 md:pb-10 lg:px-8">
        <div className="mx-auto max-w-7xl">
          {!user.email_verified && <VerifyBanner />}
          {user.plan?.status === "past_due" && (
            <div className="mb-4 flex flex-col gap-2 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between" data-testid="past-due-banner">
              <span>O pagamento da sua assinatura falhou. Atualize o cartão até {user.plan.grace_until ? `${user.plan.grace_until.slice(8, 10)}/${user.plan.grace_until.slice(5, 7)}` : "o fim do prazo"} para não voltar ao plano Gratuito.</span>
              <NavLink to="/configuracoes?tab=assinatura" className="font-semibold text-destructive hover:underline" data-testid="past-due-banner-link">Atualizar cartão</NavLink>
            </div>
          )}
          <Outlet />
        </div>
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-50 flex h-16 items-center justify-around border-t bg-card/90 px-2 backdrop-blur-lg md:hidden" data-testid="mobile-bottom-nav">
        {NAV.filter((n) => MOBILE.includes(n.key)).map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={`mobile-nav-${n.key}`}
            className={({ isActive }) => `flex flex-col items-center gap-1 px-3 py-1 text-[11px] font-medium ${isActive ? "text-primary" : "text-muted-foreground"}`}>
            <n.icon className="h-5 w-5" /> {n.label}
          </NavLink>
        ))}
        <button onClick={() => setMore(true)} className="flex flex-col items-center gap-1 px-3 py-1 text-[11px] font-medium text-muted-foreground" data-testid="mobile-nav-more">
          <Menu className="h-5 w-5" /> Mais
        </button>
      </nav>

      <Sheet open={more} onOpenChange={setMore}>
        <SheetContent side="bottom" className="rounded-t-2xl" data-testid="mobile-more-sheet">
          <SheetHeader><SheetTitle>Menu</SheetTitle></SheetHeader>
          <div className="mt-4 space-y-1">
            {NAV.filter((n) => !MOBILE.includes(n.key)).map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setMore(false)} className={navClass} data-testid={`mobile-more-${n.key}`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
            <NavLink to="/planos" onClick={() => setMore(false)} className={navClass} data-testid="mobile-more-planos">
              <Sparkles className="h-4 w-4" /> Planos
            </NavLink>
            <button onClick={doLogout} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-destructive hover:bg-muted" data-testid="mobile-logout-btn">
              <LogOut className="h-4 w-4" /> Sair
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
